import * as Mui from "@mui/material";
import * as MuiIcons from "@mui/icons-material";
import {
  ColDef,
  ICellRendererParams,
  IServerSideDatasource,
} from "ag-grid-community";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import { nameof } from "../common/ObjectUtils";
import { EquipmentAssociationTypes } from "./Types";
import StyledMui from "./StyledMui";
import Dialogs from "./dialogs";
import useToggle from "../../hooks/useToggle";
import { useMemo, useState } from "react";
import EquipmentAssociationService from "./EquipmentAssociationService";
import useSnackbar from "../../hooks/useSnackbar";
import useOperationTranslation from "../../hooks/useOperationTranslation";
import Ellipsis from "../common/Ellipsis";
import { useHasScope } from "../../../../config/globalConfig";
import AgGridOperationTable from "../common/AgGridOperationTable";

interface Props {
  tenant: string;
  table: {
    datasource: IServerSideDatasource;
    totalElements: number;
  };
  selectedAcriss?: EquipmentAssociationTypes.ACRISS;
  updateDatasourceKit: () => void;
  updateDatasourceAcriss: () => void;
}

const AssociationKitsTable = (props: Props) => {
  const hasScope = useHasScope;

  const [selectedKit, setSelectedKit] = useState<
    EquipmentAssociationTypes.Kit | undefined
  >(undefined);

  const [showDelete, toggleDelete] = useToggle();
  const [showWatchKit, toggleWatchKit] = useToggle();

  const snackbar = useSnackbar();
  const t = useOperationTranslation();

  const onOpenDelete = (kit: EquipmentAssociationTypes.Kit | undefined) => {
    setSelectedKit(kit);
    toggleDelete();
  };

  const onDelete = () => {
    if (!props.selectedAcriss) {
      snackbar.show(
        "warning",
        t(
          "vehicleEquipmentAssociation.dialogs.delete.messages.kit.warningAcriss"
        )
      );
      return;
    }

    if (!selectedKit) {
      snackbar.show(
        "warning",
        t("vehicleEquipmentAssociation.dialogs.delete.messages.kit.warningKit")
      );
      return;
    }

    EquipmentAssociationService.put
      .detachKit(props.tenant, props.selectedAcriss.id, selectedKit.id)
      .then(
        () => {
          props.updateDatasourceKit();
          snackbar.show(
            "success",
            t("vehicleEquipmentAssociation.dialogs.delete.messages.kit.success")
          );
        },
        () => {
          snackbar.show(
            "error",
            t("vehicleEquipmentAssociation.dialogs.delete.messages.kit.error")
          );
        }
      );
  };

  const onOpenWatchKit = (kit: EquipmentAssociationTypes.Kit | undefined) => {
    setSelectedKit(kit);
    toggleWatchKit();
  };

  const columns: ColDef[] = [
    {
      headerName: t(
        "vehicleEquipmentAssociation.tables.equipmentKits.columns.code"
      ),
      field: nameof<EquipmentAssociationTypes.Kit>("code"),
      flex: 1,
    },
    {
      headerName: t(
        "vehicleEquipmentAssociation.tables.equipmentKits.columns.description"
      ),
      field: nameof<EquipmentAssociationTypes.Kit>("descr"),
      flex: 1,
      cellRenderer: (
        params: ICellRendererParams<EquipmentAssociationTypes.Kit>
      ) => {
        return (
          <Mui.Tooltip title={params.value}>
            <Ellipsis>{params.value}</Ellipsis>
          </Mui.Tooltip>
        );
      },
    },
    {
      headerName: "",
      maxWidth: 80,
      cellStyle: {
        padding: "0px",
        border: "none",
      },
      cellRenderer: (
        params: ICellRendererParams<EquipmentAssociationTypes.Kit>
      ) => {
        return (
          <StyledMui.Stack direction="row">
            <Mui.Tooltip
              title={t(
                "vehicleEquipmentAssociation.tables.equipmentKits.actions.watch"
              )}
            >
              <Mui.IconButton
                color="accent"
                onClick={() => onOpenWatchKit(params.data)}
              >
                <MuiIcons.Description />
              </Mui.IconButton>
            </Mui.Tooltip>

            {hasScope("SCOPE_UPDATE_MDVEHICLE_EQUIPMENTKIT") && (
              <Mui.Tooltip
                title={t(
                  "vehicleEquipmentAssociation.tables.equipmentKits.actions.delete"
                )}
              >
                <Mui.IconButton
                  color="accent"
                  onClick={() => onOpenDelete(params.data)}
                >
                  <MuiIcons.Delete />
                </Mui.IconButton>
              </Mui.Tooltip>
            )}
          </StyledMui.Stack>
        );
      },
    },
  ];

  /** LABELS */
  const deleteContent = useMemo<string>(() => {
    return t("vehicleEquipmentAssociation.dialogs.delete.content.kits")
      .replace("#kitElement", `${selectedKit?.code} - ${selectedKit?.descr}`)
      .replace(
        "#acrissElement",
        `${props.selectedAcriss?.code || ""} - ${
          props.selectedAcriss?.label || ""
        }`
      );
  }, [props.selectedAcriss, selectedKit]);

  const emptyStateTitle = useMemo<string>(() => {
    return !props.selectedAcriss
      ? t(
          "vehicleEquipmentAssociation.tables.equipmentKits.emptyState.noAcriss.title"
        )
      : t(
          "vehicleEquipmentAssociation.tables.equipmentKits.emptyState.noKits.title"
        );
  }, [props.table.totalElements]);

  const emptyStateContent = useMemo<string>(() => {
    return !props.selectedAcriss
      ? t(
          "vehicleEquipmentAssociation.tables.equipmentKits.emptyState.noAcriss.content"
        )
      : t(
          "vehicleEquipmentAssociation.tables.equipmentKits.emptyState.noKits.content"
        );
  }, [props.table.totalElements]);

  return (
    <Mui.Box>
      <AgGridOperationTable
        columnDefs={columns}
        serverSideDatasource={props.table.datasource}
        serverSideInfiniteScroll={true}
        rowModelType="serverSide"
        cacheBlockSize={10}
        totalElements={props.table.totalElements}
        emptyState={{
          title:emptyStateTitle,
          content:emptyStateContent
        }}
      />


      <Dialogs.DeleteConfirmation
        open={showDelete}
        onClose={toggleDelete}
        title={t("vehicleEquipmentAssociation.dialogs.delete.title.kits")}
        content={deleteContent}
        deleteBtnText={t(
          "vehicleEquipmentAssociation.dialogs.delete.actions.deleteKit"
        )}
        onDelete={onDelete}
      />

      <Dialogs.WatchEquipmentKit
        tenant={props.tenant}
        open={showWatchKit}
        onClose={toggleWatchKit}
        kit={selectedKit}
      />
    </Mui.Box>
  );
};

export default AssociationKitsTable;
